import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CatalogEntryInput,
  CatalogEntryUpdate,
  CatalogName,
} from "@core/types";
import * as api from "./api";

const key = (catalog: CatalogName, includeInactive: boolean, search: string) =>
  ["portal", "catalogs", catalog, includeInactive, search] as const;
export const useCatalogs = () =>
  useQuery({ queryKey: ["portal", "catalogs"], queryFn: api.listCatalogs });
export const useEntries = (
  catalog: CatalogName,
  includeInactive: boolean,
  search: string,
) =>
  useQuery({
    queryKey: key(catalog, includeInactive, search),
    queryFn: () => api.listEntries(catalog, { includeInactive, search }),
  });

// Talleres y comercios solo guardan cityId (uuid); el catálogo de ciudades ya
// se consulta en el formulario, así que reutilizamos el mismo join para
// mostrar el nombre en las listas especializadas en vez de inventar un
// campo que la API no expone.
export function useCityNames() {
  const cities = useEntries("cities", false, "");
  const map = useMemo(() => {
    const next = new Map<string, string>();
    for (const city of cities.data ?? []) {
      next.set(
        city.id,
        typeof city.name === "string" ? city.name : city.name.es,
      );
    }
    return next;
  }, [cities.data]);
  return { map, isLoading: cities.isLoading, isError: cities.isError };
}
export function useCatalogMutations(catalog: CatalogName) {
  const client = useQueryClient();
  const invalidate = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: ["portal", "catalogs"] }),
      client.invalidateQueries({
        queryKey: ["portal", "catalogs", catalog],
      }),
      ...(catalog === "merchants" || catalog === "workshops"
        ? [
            client.invalidateQueries({
              queryKey: ["portal", "catalog-entries", catalog, "active"],
            }),
          ]
        : []),
    ]);
  return {
    create: useMutation({
      mutationFn: (input: CatalogEntryInput) => api.createEntry(catalog, input),
      onSuccess: invalidate,
    }),
    edit: useMutation({
      mutationFn: ({ id, input }: { id: string; input: CatalogEntryUpdate }) =>
        api.editEntry(catalog, id, input),
      onSuccess: invalidate,
    }),
    inactivate: useMutation({
      mutationFn: (id: string) => api.inactivateEntry(catalog, id),
      onSuccess: invalidate,
    }),
  };
}
